import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Modal, Input, Spin, Empty } from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { apiColegas, authHeader } from "../../redux/actions/catalogos/catalogosApi";
import EntidadFotoCard from "./EntidadFotoCard";
import ActividadTab from "./ActividadTab";

import "./PaquetesPage.css";
import "./EventoDetallePage.css";
import { usePermisos } from "../../context/PermisosContext";

export default function ColegaDetallePage() {
  const { idColega } = useParams();
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [colega, setColega] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchColega = useCallback(() => {
    setLoading(true);
    apiColegas
      .get(`/colegas/${idColega}`, { headers: authHeader() })
      .then((res) => setColega(res.data))
      .catch(() => toast.error("No se pudo cargar el colega"))
      .finally(() => setLoading(false));
  }, [idColega]);

  useEffect(() => { fetchColega(); }, [fetchColega]);

  const startEditInfo = () => {
    setEditForm({
      nombre: colega.nombre,
      especialidad: colega.especialidad,
      telefono: colega.telefono,
      correo: colega.correo,
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (!editForm.nombre?.trim()) return toast.warning("El nombre es obligatorio");
    setSavingInfo(true);
    try {
      await apiColegas.put(`/colegas/${idColega}`, editForm, { headers: authHeader() });
      toast.success("Colega actualizado correctamente");
      setEditingInfo(false);
      fetchColega();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el colega");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleFoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await apiColegas.post(`/colegas/${idColega}/foto`, fd, { headers: authHeader() });
    if (res.data?.item) setColega(res.data.item);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "¿Eliminar este colega?",
      content: `"${colega.nombre}" se eliminara del directorio. Esta accion no se puede deshacer.`,
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await apiColegas.delete(`/colegas/${idColega}`, { headers: authHeader() });
          toast.success("Colega eliminado");
          navigate("/app/colegas");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar el colega");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!colega) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Colega no encontrado" />
      </div>
    );
  }

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <h1 className="cd-client-name">{(colega.nombre || "—").toUpperCase()}</h1>
              <div className="cd-header-meta">
                {colega.especialidad && <span className="cd-meta-item"><i className="fa-solid fa-stethoscope" /> {colega.especialidad}</span>}
                <span className="cd-meta-item">
                  <i className="fa-solid fa-circle" style={{ fontSize: 8, color: colega.active ? "#15803d" : "#b45309" }} />
                  {colega.active ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>
            <div className="cd-header-actions">
              {perm("colegas", "eliminar") && (
                <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
        <div className="cd-info-layout">
          <EntidadFotoCard item={colega} icon="fa-user-doctor" canEdit={perm("colegas", "editar")} onUpload={handleFoto} />
          <div style={{ flex: 1, minWidth: 0 }}>
        <div className="cd-card">
          <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-user-doctor"></i></div>
              <h2 className="cd-card-title">Informacion del colega</h2>
            </div>
            {!editingInfo && perm("colegas", "editar") && (
                <Button className="cd-btn-edit" onClick={startEditInfo}>Editar</Button>
)}
          </div>

          {editingInfo ? (
            <div>
              <div className="cd-edit-grid">
                <div className="cd-edit-field">
                  <span className="cd-edit-label">Nombre</span>
                  <Input value={editForm.nombre} onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} />
                </div>
                <div className="cd-edit-field">
                  <span className="cd-edit-label">Especialidad</span>
                  <Input value={editForm.especialidad || ""} onChange={(e) => setEditForm((f) => ({ ...f, especialidad: e.target.value }))} />
                </div>
                <div className="cd-edit-field">
                  <span className="cd-edit-label">Telefono</span>
                  <Input value={editForm.telefono || ""} onChange={(e) => setEditForm((f) => ({ ...f, telefono: e.target.value }))} />
                </div>
                <div className="cd-edit-field">
                  <span className="cd-edit-label">Correo</span>
                  <Input value={editForm.correo || ""} onChange={(e) => setEditForm((f) => ({ ...f, correo: e.target.value }))} />
                </div>
              </div>
              <div className="cd-edit-actions">
                <Button icon={<CloseOutlined />} onClick={() => setEditingInfo(false)} disabled={savingInfo}>Cancelar</Button>
                <Button type="primary" icon={<SaveOutlined />} loading={savingInfo} onClick={handleSaveInfo}>Guardar</Button>
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
              <div><span className="cd-field-label">Nombre</span><span className="cd-field-value">{colega.nombre}</span></div>
              <div><span className="cd-field-label">Especialidad</span><span className="cd-field-value">{colega.especialidad || "—"}</span></div>
              <div><span className="cd-field-label">Telefono</span><span className="cd-field-value">{colega.telefono || "—"}</span></div>
              <div><span className="cd-field-label">Correo</span><span className="cd-field-value">{colega.correo || "—"}</span></div>
              <div><span className="cd-field-label">Estado</span><span className="cd-field-value" style={{ color: colega.active ? "#15803d" : "#b45309" }}>{colega.active ? "Activo" : "Inactivo"}</span></div>
            </div>
          )}
        </div>
          </div>
        </div>
        )}

        {activeTab === "actividad" && (
          <ActividadTab
            titulo="Actividad del colega"
            fetcher={() => apiColegas.get(`/colegas/${idColega}/actividad`, { headers: authHeader() })}
          />
        )}
      </div>
    </div>
  );
}
